/**
 * useSkuInvoice — POST /api/v1/skus/:code/invoice, then open the Stars invoice
 * inside Telegram WebApp via window.Telegram.WebApp.openInvoice().
 *
 * The bot's pre_checkout_query handler validates the embedded sku_code; on
 * successful_payment it dispatches the avatar-worker job(s).
 *
 * Generic TInput allows callers to pass any run-input payload as the POST body.
 * Defaults to Record<string, unknown> for untyped usage.
 *
 * Returns:
 *   purchase(skuCode, runInput: TInput): Promise<InvoiceStatus>
 *   state: 'idle' | 'loading' | 'opening' | 'error'
 *   lastStatus: invoice close status from Telegram ('paid'|'cancelled'|'failed'|'pending'|null)
 */

import { useCallback, useState } from 'react';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

type TelegramInvoiceStatus = 'paid' | 'cancelled' | 'failed' | 'pending';

interface InvoiceResponse {
  title: string;
  description: string;
  stars: number;
  currency: string;
  /** Real Telegram invoice link (https://t.me/$<slug>) for openInvoice(). */
  invoiceLink: string;
  invoicePayload?: string;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        openInvoice?: (url: string, cb?: (status: string) => void) => void;
      };
    };
  }
}

export class SkuInvoiceError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'SkuInvoiceError';
  }
}

export interface PurchaseResult {
  status: TelegramInvoiceStatus | null;
  /** AvatarPack IDs dispatched by the mock-payment branch (empty in real Stars flow). */
  packIds: string[];
  /** PastLivesRun IDs dispatched by mock-payment for past_lives SKU (empty otherwise). */
  runIds?: string[];
  /**
   * Inline text result returned by redeem-coupon for text-generation SKUs
   * (e.g. brief_topic). Undefined for avatar/image-gen SKUs.
   */
  text?: string;
}

export function useSkuInvoice<TInput = Record<string, unknown>>() {
  const jwt = useAuth((s) => s.jwt);
  const [state, setState] = useState<'idle' | 'loading' | 'opening' | 'error'>('idle');
  const [lastStatus, setLastStatus] = useState<TelegramInvoiceStatus | null>(null);
  const [lastPackIds, setLastPackIds] = useState<string[] | null>(null);

  const purchase = useCallback(
    async (skuCode: string, runInput: TInput): Promise<PurchaseResult> => {
      if (import.meta.env.VITE_MOCK_PAYMENTS === '1') {
        setLastPackIds(null);
        let packIds: string[] = [];
        let runIds: string[] = [];
        if (jwt) {
          try {
            const res = await apiFetch(
              `/api/v1/skus/${skuCode}/dev-trigger-run`,
              jwt,
              {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify(runInput ?? {}),
              },
            );
            if (res.ok) {
              const data = (await res.json()) as { packIds?: string[]; runIds?: string[] };
              if (Array.isArray(data.packIds)) {
                packIds = data.packIds;
                setLastPackIds(data.packIds);
              }
              if (Array.isArray(data.runIds)) runIds = data.runIds;
            } else {
              setState('error');
              const text = await res.text().catch(() => '');
              throw new SkuInvoiceError(res.status, `dev_trigger_failed_${res.status}: ${text}`);
            }
          } catch (err) {
            if (err instanceof SkuInvoiceError) throw err;
            // Network error: surface to caller so it can show an error state.
            setState('error');
            throw new SkuInvoiceError(0, `dev_trigger_network_error: ${(err as Error).message}`);
          }
        }
        setLastStatus('paid');
        setState('idle');
        return { status: 'paid', packIds, runIds };
      }
      if (!jwt) throw new SkuInvoiceError(401, 'missing_authorization');

      setState('loading');
      try {
        const res = await apiFetch(`/api/v1/skus/${skuCode}/invoice`, jwt, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(runInput),
        });
        if (!res.ok) {
          setState('error');
          const text = await res.text().catch(() => '');
          throw new SkuInvoiceError(res.status, `invoice_failed_${res.status}: ${text}`);
        }
        const data = (await res.json()) as InvoiceResponse;

        setState('opening');
        // openInvoice() requires the real https://t.me/$<slug> link from the
        // server's createInvoiceLink — a tg://invoice?payload= URL is rejected
        // with WebAppInvoiceUrlInvalid.
        const invoiceUrl = data.invoiceLink;
        if (!invoiceUrl) {
          setState('error');
          throw new SkuInvoiceError(502, 'invoice_link_missing');
        }
        const openInvoice = window.Telegram?.WebApp?.openInvoice;
        if (!openInvoice) {
          setState('error');
          throw new SkuInvoiceError(500, 'telegram_webapp_unavailable');
        }

        return await new Promise<PurchaseResult>((resolve) => {
          openInvoice(invoiceUrl, (status: string) => {
            const normalized = (['paid', 'cancelled', 'failed', 'pending'] as const).includes(
              status as TelegramInvoiceStatus,
            )
              ? (status as TelegramInvoiceStatus)
              : null;
            setLastStatus(normalized);
            setState('idle');
            // Real Stars flow: dispatch happens in the bot's successful_payment
            // webhook, not here — the miniapp polls /me/avatar-packs to find
            // the freshly created pack. No packIds returned synchronously.
            resolve({ status: normalized, packIds: [], runIds: [] });
          });
        });
      } catch (err) {
        setState('error');
        throw err;
      }
    },
    [jwt],
  );

  /**
   * Apply a discount coupon instead of paying with Stars. A valid 100%-free code
   * dispatches the SKU directly and returns the same { status:'paid', packIds,
   * runIds } shape as the mock-payment branch, so the caller's poll loop is
   * unchanged. Throws SkuInvoiceError on invalid/exhausted/non-free codes — the
   * caller maps res.status (422 invalid, 429 exhausted) to copy.
   */
  const redeemCoupon = useCallback(
    async (skuCode: string, code: string, runInput: TInput): Promise<PurchaseResult> => {
      if (!jwt) throw new SkuInvoiceError(401, 'missing_authorization');
      setState('loading');
      try {
        const res = await apiFetch(`/api/v1/skus/${skuCode}/redeem-coupon`, jwt, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ code, runInput: runInput ?? {} }),
        });
        if (!res.ok) {
          setState('error');
          const text = await res.text().catch(() => '');
          throw new SkuInvoiceError(res.status, `coupon_failed_${res.status}: ${text}`);
        }
        const data = (await res.json()) as { packIds?: string[]; runIds?: string[]; text?: string };
        const packIds = Array.isArray(data.packIds) ? data.packIds : [];
        const runIds = Array.isArray(data.runIds) ? data.runIds : [];
        if (packIds.length) setLastPackIds(packIds);
        setLastStatus('paid');
        setState('idle');
        return { status: 'paid', packIds, runIds, text: data.text ?? undefined };
      } catch (err) {
        setState('error');
        throw err;
      }
    },
    [jwt],
  );

  return { purchase, redeemCoupon, state, lastStatus, lastPackIds };
}
